<template>
  <div>
    <v-dialog v-model="dialog" eager persistent max-width="480">
      <v-form ref="AssignRoleRef" @submit.prevent>
        <v-card class="dialog-card" rounded="xl" elevation="4">
          <!-- ── Header ── -->
          <div class="pa-5 pb-4">
            <div class="d-flex justify-space-between align-center">
              <div class="dialog-title">Assign Role</div>
              <v-btn
                icon="mdi-close"
                variant="text"
                size="small"
                density="comfortable"
                color="#9ca3af"
                @click="closeD()"
              />
            </div>

            <!-- User context row -->
            <div
              class="user-row rounded-lg mt-3 pa-3 d-flex align-center"
              style="gap: 10px"
            >
              <div
                class="avatar rounded-lg d-flex align-center justify-center flex-shrink-0"
              >
                {{ initials }}
              </div>
              <div style="min-width: 0">
                <div class="user-name text-truncate">{{ name }}</div>
                <div class="user-email text-truncate">{{ email }}</div>
              </div>
            </div>
          </div>

          <v-divider />

          <div class="pa-5">
            <!-- ── Roles ── -->
            <div v-if="shouldShow" class="field-label mb-2">Roles</div>

            <div
              v-if="shouldShow"
              class="role-row rounded-lg pa-3 mb-2 d-flex align-center"
              :class="getRoleCoordinator ? 'role-row--on' : 'role-row--off'"
              style="gap: 12px; cursor: pointer"
              @click="getRoleCoordinator = !getRoleCoordinator"
            >
              <v-icon
                size="18"
                :color="getRoleCoordinator ? '#10b981' : '#d1d5db'"
              >
                {{
                  getRoleCoordinator
                    ? "mdi-checkbox-marked-circle"
                    : "mdi-checkbox-blank-circle-outline"
                }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="role-name">OJT Coordinator</div>
                <div class="role-desc">Manages students and partner HTEs</div>
              </div>
            </div>

            <div
              v-if="shouldShow"
              class="role-row rounded-lg pa-3 mb-5 d-flex align-center"
              :class="getRoleInstructor ? 'role-row--on' : 'role-row--off'"
              style="gap: 12px; cursor: pointer"
              @click="getRoleInstructor = !getRoleInstructor"
            >
              <v-icon
                size="18"
                :color="getRoleInstructor ? '#10b981' : '#d1d5db'"
              >
                {{
                  getRoleInstructor
                    ? "mdi-checkbox-marked-circle"
                    : "mdi-checkbox-blank-circle-outline"
                }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="role-name">OJT Instructor</div>
                <div class="role-desc">
                  Monitors and validates intern submissions
                </div>
              </div>
            </div>

            <!-- ── Program ── -->
            <div class="field-label">Program Assignment</div>

            <v-autocomplete
              v-model="programs_assigned"
              :rules="[formRules.required]"
              item-value="id"
              item-title="name"
              :items="programs_list"
              density="compact"
              variant="outlined"
              :multiple="shouldShow"
              closable-chips
              chips
              placeholder="Select one or more programs..."
              class="prog-field"
            />
          </div>

          <v-divider />

          <!-- ── Footer ── -->
          <div class="pa-4 d-flex justify-end" style="gap: 8px">
            <v-btn
              variant="text"
              color="#6b7280"
              rounded="lg"
              @click="closeD()"
            >
              Cancel
            </v-btn>
            <v-btn
              color="#10b981"
              variant="flat"
              rounded="lg"
              class="text-white px-5"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="save()"
            >
              Save Changes
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";

export default {
  props: {
    data: Object,
    action: String,
    isProgChair: Boolean,
  },
  data() {
    return {
      name: null,
      email: null,
      programs_list: [],
      programs_assigned: [],
      usertypeID: null,
      dialog: false,
      roles: [],
      btnLoading: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "System Message",
        message: "",
        top: 10,
      },
    };
  },
  watch: {
    data: {
      handler(data) {
        console.log(data);
        this.dialog = true;
        this.initialize();
        this.name = data.name;
        this.email = data.email;
        this.programs_assigned = data.assigned_as_instructor_id;
        this.roles = data.user_roleID == null ? [] : [...data.user_roleID];
        this.usertypeID = data.usertypeID;
        this.$nextTick(() => {
          this.$refs.AssignRoleRef?.resetValidation();
        });
      },
      deep: true,
    },
  },
  computed: {
    shouldShow() {
      return this.usertypeID != 10;
    },
    initials() {
      if (!this.name) return "?";
      const parts = this.name.trim().split(" ");
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0][0].toUpperCase();
    },
    getRoleCoordinator: {
      get() {
        return this.roles.includes(1);
      },
      set(val) {
        if (val) {
          if (!this.roles.includes(1)) this.roles.push(1);
          if (!this.roles.includes(2)) this.roles.push(2);
        } else {
          this.roles = this.roles.filter((r) => r !== 1);
        }
      },
    },
    getRoleInstructor: {
      get() {
        return this.roles.includes(2);
      },
      set(val) {
        if (val) {
          if (!this.roles.includes(2)) this.roles.push(2);
        } else {
          if (this.roles.includes(1) && this.roles.includes(2)) {
            this.roles = [];
          } else {
            this.roles = this.roles.filter((r) => r !== 2);
          }
        }
      },
    },
  },
  methods: {
    initialize() {
      this.axiosCall("/program/getProgramsByInstitute", "GET").then((res) => {
        if (res) this.programs_list = res.data;
      });
    },

    async save() {
      const formValid = await this.$refs.AssignRoleRef?.validate();
      if (formValid?.valid) {
        this.btnLoading = true;
        this.axiosCall("/user-details/setRole", "PATCH", {
          role: this.roles,
          programs: this.programs_assigned,
          user_id: this.data.id,
        }).then((res) => {
          this.btnLoading = false;
          this.fadeAwayMessage.show = true;
          if (res.data.status == 200) {
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.message = res.data.msg;
            this.closeD();
          } else {
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.message = "Please fill in all required fields.";
      }
    },

    closeD() {
      eventBus.emit("closeAssignRoleDialog", true);
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
/* ── Card ── */
.dialog-card {
  overflow: hidden;
}

/* ── Header ── */
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

/* ── User row ── */
.user-row {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.avatar {
  width: 36px;
  height: 36px;
  background: #ecfdf5;
  color: #059669;
  font-size: 13px;
  font-weight: 700;
}
.user-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.user-email {
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
}

/* ── Field labels ── */
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

/* ── Role rows ── */
.role-row {
  border: 1px solid #e5e7eb;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}
.role-row--off {
  background: #ffffff;
}
.role-row--off:hover {
  border-color: #d1d5db;
  background: #fafafa;
}
.role-row--on {
  background: #f0fdf4;
  border-color: #6ee7b7;
}
.role-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.role-desc {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 1px;
}

/* ── Program field ── */
.prog-field :deep(.v-field) {
  border-radius: 8px;
}
.prog-field :deep(.v-field__outline__start),
.prog-field :deep(.v-field__outline__notch),
.prog-field :deep(.v-field__outline__end) {
  border-color: #e2e8f0 !important;
}
.prog-field :deep(.v-field--focused .v-field__outline__start),
.prog-field :deep(.v-field--focused .v-field__outline__notch),
.prog-field :deep(.v-field--focused .v-field__outline__end) {
  border-color: #10b981 !important;
}
</style>
